import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, Text, Center } from '@react-three/drei';
import * as THREE from 'three';
import { useSlotTheme, useShouldRender3D } from '../../hooks/useSlotTheme';
import type { SymbolId, SymbolDefinition } from '../../themes/types';
import type { ResolvedSkinSymbol, ResolvedReelspotBackground, WinAnimationPresetId } from '../../themes/skin-types';
import { useWinAnimation, getMergedAnimationConfig, DEFAULT_ANIMATION_CONFIGS } from '../../hooks/useWinAnimation';
import { ReelspotBackground } from './ReelspotBackground';

// Inter Bold font from CDN - more reliable than local file
const FONT_URL = 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2';

// =============================================================================
// Types
// =============================================================================

interface Symbol3DProps {
  /** Symbol ID to render */
  symbolId: SymbolId;
  /** Position in 3D space */
  position?: [number, number, number];
  /** Scale multiplier */
  scale?: number;
  /** Whether the symbol is part of a winning combination */
  isWinning?: boolean;
  /** Glow intensity for winning symbols (0-1) */
  glowIntensity?: number;
  /** Whether the symbol should animate (spin/bounce) */
  animate?: boolean;
}

/**
 * Props for the enhanced reelspot-wrapped symbol
 */
export interface ReelspotSymbol3DProps {
  /** Symbol ID to render */
  symbolId: SymbolId;
  /** Position in 3D space */
  position?: [number, number, number];
  /** Scale multiplier */
  scale?: number;
  /** Whether the symbol is part of a winning combination */
  isWinning?: boolean;
  /** Glow intensity for winning symbols (0-1) */
  glowIntensity?: number;
  /** Whether the symbol should animate */
  animate?: boolean;
  /** Reelspot background configuration (from dynamic skin) */
  reelspotBackground?: ResolvedReelspotBackground | null;
  /** Win animation preset ID */
  winAnimationType?: WinAnimationPresetId;
  /** Win animation configuration overrides */
  winAnimationConfig?: Record<string, unknown>;
  /** Width of the reelspot */
  reelspotWidth?: number;
  /** Height of the reelspot */
  reelspotHeight?: number;
  /** Optional resolved symbol (when using dynamic skin) */
  resolvedSymbol?: ResolvedSkinSymbol;
}

// =============================================================================
// 3D Symbol Component
// =============================================================================

export function Symbol3D({
  symbolId,
  position = [0, 0, 0],
  scale = 1,
  isWinning = false,
  glowIntensity = 0,
  animate = false,
}: Symbol3DProps) {
  const { getSymbol } = useSlotTheme();
  const shouldRender3D = useShouldRender3D();
  const meshRef = useRef<THREE.Group>(null);

  const symbol = getSymbol(symbolId);

  // Animation state
  useFrame((state) => {
    if (!meshRef.current) return;

    if (isWinning) {
      // Pulse scale for winning symbols
      const pulse = Math.sin(state.clock.elapsedTime * 4) * 0.05 + 1;
      meshRef.current.scale.setScalar(scale * pulse);
    }

    if (animate) {
      // Subtle float animation
      meshRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.02;
    }
  });

  if (!symbol) {
    // Fallback for unknown symbol
    return <PlaceholderSymbol position={position} scale={scale} text={symbolId} />;
  }

  if (!shouldRender3D) {
    // 2D sprite fallback
    return (
      <SpriteSymbol
        symbol={symbol}
        position={position}
        scale={scale}
        isWinning={isWinning}
      />
    );
  }

  // Check if this is a text-based symbol
  if (symbol.renderMode === 'text3d' && symbol.textContent) {
    return (
      <group ref={meshRef} position={position} scale={scale}>
        <Text3DSymbol
          symbol={symbol}
          isWinning={isWinning}
          glowIntensity={glowIntensity}
        />
      </group>
    );
  }

  return (
    <group ref={meshRef} position={position} scale={scale}>
      {/* Try to load 3D model, fallback to placeholder */}
      <ModelSymbol
        symbol={symbol}
        isWinning={isWinning}
        glowIntensity={glowIntensity}
      />
    </group>
  );
}

// =============================================================================
// Model-based Symbol
// =============================================================================

interface ModelSymbolProps {
  symbol: SymbolDefinition;
  isWinning: boolean;
  glowIntensity: number;
}

function ModelSymbol({ symbol, isWinning, glowIntensity }: ModelSymbolProps) {
  const { getAssetUrl } = useSlotTheme();

  // Attempt to load the 3D model
  const modelUrl = getAssetUrl(symbol.model);

  // Always call hooks unconditionally
  const gltf = useGLTF(modelUrl);
  const { scene } = gltf;

  // Get modelScale from symbol config
  const modelScale = symbol.modelScale ?? 1;

  const clonedScene = useMemo(() => {
    // Get rotation from symbol config (inside useMemo to avoid dependency issues)
    const rotation = symbol.modelRotation ?? { x: 0, y: 0, z: 0 };
    if (!scene) return null;

    // Debug: Log all object names in the GLB (helpful for identifying child objects)
    if (process.env.NODE_ENV === 'development') {
      const objectNames: string[] = [];
      scene.traverse((child) => {
        if (child.name) {
          objectNames.push(`${child.type}: "${child.name}"`);
        }
      });
      if (objectNames.length > 0) {
        console.log(`[Symbol3D] GLB objects for "${symbol.id}":`, objectNames);
      }
    }

    let targetScene: THREE.Object3D;

    // If modelChildName is specified, find and use only that child
    if (symbol.modelChildName) {
      const foundChild = scene.getObjectByName(symbol.modelChildName);
      if (foundChild) {
        targetScene = foundChild.clone();

        // Update world matrix for accurate bounding box calculation
        targetScene.updateMatrixWorld(true);

        // When extracting a child from a multi-object GLB, we need to:
        // 1. Reset its position to origin (child may be positioned elsewhere in scene)
        // 2. Center based on bounding box
        // 3. Normalize scale to fit in symbol slot
        const box = new THREE.Box3().setFromObject(targetScene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Move to origin and center
        targetScene.position.set(-center.x, -center.y, -center.z);

        // Wrap in a group so we can apply centering without affecting the object's internal structure
        const wrapper = new THREE.Group();
        wrapper.add(targetScene);

        // Normalize scale to fit in roughly a 0.8 unit cube, then apply modelScale
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) {
          const normalizeScale = (0.8 / maxDim) * modelScale;
          wrapper.scale.setScalar(normalizeScale);
        }

        targetScene = wrapper;
      } else {
        console.warn(`[Symbol3D] Child "${symbol.modelChildName}" not found in "${symbol.id}" model, using full scene`);
        // Still normalize even when child not found
        const cloned = scene.clone();
        cloned.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(cloned);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        cloned.position.set(-center.x, -center.y, -center.z);
        const wrapper = new THREE.Group();
        wrapper.add(cloned);
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0.01 && isFinite(maxDim)) {
          const normalizeScale = Math.min(Math.max((0.7 / maxDim) * modelScale, 0.01), 10);
          wrapper.scale.setScalar(normalizeScale);
        }
        targetScene = wrapper;
      }
    } else {
      // Clone and auto-normalize all models to fit in symbol slot
      const cloned = scene.clone();
      cloned.updateMatrixWorld(true);

      // Calculate bounding box - only include visible mesh objects
      const box = new THREE.Box3();
      cloned.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          const childBox = new THREE.Box3().setFromObject(child);
          if (childBox.isEmpty() === false) {
            box.union(childBox);
          }
        }
      });

      // Fallback if no meshes found
      if (box.isEmpty()) {
        box.setFromObject(cloned);
      }

      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Move model so its center is at origin
      cloned.position.set(-center.x, -center.y, -center.z);

      // Wrap in a group for proper positioning
      const wrapper = new THREE.Group();
      wrapper.add(cloned);

      // Normalize scale to fit in symbol slot, then apply modelScale multiplier
      const maxDim = Math.max(size.x, size.y, size.z);
      if (maxDim > 0.01 && isFinite(maxDim)) {
        // Normalize to fit in ~0.7 unit cube, multiplied by modelScale
        const normalizeScale = Math.min(Math.max((0.7 / maxDim) * modelScale, 0.01), 10);
        wrapper.scale.setScalar(normalizeScale);
      }

      targetScene = wrapper;
    }

    // Apply rotation from config
    targetScene.rotation.set(
      (rotation.x * Math.PI) / 180,
      (rotation.y * Math.PI) / 180,
      (rotation.z * Math.PI) / 180
    );

    // Apply win glow effect
    if (isWinning && symbol.glowColor) {
      targetScene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const material = child.material as THREE.MeshStandardMaterial;
          if (material) {
            material.emissive = new THREE.Color(symbol.glowColor);
            material.emissiveIntensity = glowIntensity * 0.5;
          }
        }
      });
    }

    return targetScene;
  }, [scene, isWinning, glowIntensity, symbol.glowColor, symbol.id, symbol.modelChildName, symbol.modelRotation, modelScale]);

  if (!clonedScene) {
    // Model failed to load, use placeholder
    return (
      <PlaceholderSymbol
        position={[0, 0, 0]}
        scale={1}
        text={symbol.name}
        color={symbol.glowColor}
      />
    );
  }

  // Don't set scale/rotation on primitive - it's already applied in the useMemo
  return <primitive object={clonedScene} />;
}

// =============================================================================
// Text-based 3D Symbol (for RUMIO letters and WILD)
// =============================================================================

interface Text3DSymbolProps {
  symbol: SymbolDefinition;
  isWinning: boolean;
  glowIntensity: number;
}

function Text3DSymbol({ symbol, isWinning, glowIntensity }: Text3DSymbolProps) {
  const meshRef = useRef<THREE.Group>(null);
  const textContent = symbol.textContent || symbol.name.charAt(0);
  const isWildSymbol = textContent.length > 1; // "WILD" vs single letter

  // Animation for the text
  useFrame((state) => {
    if (!meshRef.current) return;

    if (isWinning) {
      // Rotate slightly when winning
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 3) * 0.1;
    }
  });

  // Calculate text size based on content
  const fontSize = isWildSymbol ? 0.18 : 0.45;

  // Material color from symbol config or default
  const baseColor = symbol.glowColor || '#FFD700';
  const emissiveColor = isWinning ? baseColor : '#000000';
  const emissiveIntensity = isWinning ? glowIntensity * 0.5 : (isWildSymbol ? 0.2 : 0);

  return (
    <group ref={meshRef}>
      {/* Background plate for better visibility */}
      <mesh position={[0, 0, -0.08]}>
        <boxGeometry args={[0.75, 0.75, 0.05]} />
        <meshStandardMaterial
          color="#1a1a2e"
          metalness={0.3}
          roughness={0.8}
        />
      </mesh>

      {/* Main 3D text using extruded geometry simulation */}
      <Center>
        <Text
          fontSize={fontSize}
          color={baseColor}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#000000"
          font={FONT_URL}
          characters="RUMIOWLD"
        >
          {textContent}
          <meshStandardMaterial
            color={baseColor}
            metalness={isWildSymbol ? 0.9 : 0.7}
            roughness={isWildSymbol ? 0.1 : 0.3}
            emissive={emissiveColor}
            emissiveIntensity={emissiveIntensity}
          />
        </Text>
      </Center>

      {/* Depth layer for pseudo-3D effect */}
      {Array.from({ length: 3 }).map((_, i) => (
        <Center key={i} position={[0, 0, -0.01 * (i + 1)]}>
          <Text
            fontSize={fontSize}
            color="#000000"
            anchorX="center"
            anchorY="middle"
            font={FONT_URL}
            characters="RUMIOWLD"
          >
            {textContent}
            <meshBasicMaterial color="#111111" transparent opacity={0.5} />
          </Text>
        </Center>
      ))}

      {/* Golden ring border for WILD symbol */}
      {isWildSymbol && (
        <mesh position={[0, 0, -0.03]}>
          <torusGeometry args={[0.4, 0.03, 8, 32]} />
          <meshStandardMaterial
            color="#FFD700"
            metalness={0.95}
            roughness={0.05}
            emissive={isWinning ? '#FFD700' : '#332200'}
            emissiveIntensity={isWinning ? 0.5 : 0.2}
          />
        </mesh>
      )}

      {/* Corner decorations for letters */}
      {!isWildSymbol && (
        <>
          {/* Subtle colored glow ring */}
          <mesh position={[0, 0, -0.06]} rotation={[0, 0, Math.PI / 4]}>
            <ringGeometry args={[0.32, 0.36, 4]} />
            <meshStandardMaterial
              color={baseColor}
              metalness={0.8}
              roughness={0.2}
              emissive={isWinning ? baseColor : '#000000'}
              emissiveIntensity={isWinning ? 0.3 : 0}
              side={THREE.DoubleSide}
            />
          </mesh>
        </>
      )}
    </group>
  );
}

// =============================================================================
// Sprite-based Symbol (2D Fallback)
// =============================================================================

interface SpriteSymbolProps {
  symbol: SymbolDefinition;
  position: [number, number, number];
  scale: number;
  isWinning: boolean;
}

function SpriteSymbol({ symbol, position, scale, isWinning }: SpriteSymbolProps) {
  const spriteRef = useRef<THREE.Sprite>(null);

  useFrame((state) => {
    if (!spriteRef.current || !isWinning) return;

    // Pulse effect for winning
    const pulse = Math.sin(state.clock.elapsedTime * 4) * 0.1 + 1;
    spriteRef.current.scale.setScalar(scale * pulse);
  });

  // For now, use a placeholder since sprites need texture loading
  return (
    <PlaceholderSymbol
      position={position}
      scale={scale}
      text={symbol.name}
      color={symbol.glowColor}
    />
  );
}

// =============================================================================
// Placeholder Symbol (Development/Fallback)
// =============================================================================

interface PlaceholderSymbolProps {
  position: [number, number, number];
  scale: number;
  text: string;
  color?: string;
}

function PlaceholderSymbol({
  position,
  scale,
  text,
  color = '#666',
}: PlaceholderSymbolProps) {
  return (
    <group position={position} scale={scale}>
      {/* Symbol background */}
      <mesh>
        <boxGeometry args={[0.8, 0.8, 0.1]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.7} />
      </mesh>

      {/* Symbol text */}
      <Text
        position={[0, 0, 0.06]}
        fontSize={0.15}
        color="white"
        anchorX="center"
        anchorY="middle"
        maxWidth={0.7}
      >
        {text.substring(0, 6)}
      </Text>
    </group>
  );
}

// =============================================================================
// Reelspot Symbol 3D - Enhanced symbol with background and win animations
// =============================================================================

/**
 * Enhanced symbol component that wraps the symbol with a reelspot background
 * and applies configurable win animations to the entire reelspot group.
 *
 * This is the "reelspot" concept where the symbol and its background cell
 * are animated together as a single unit.
 *
 * @example
 * ```tsx
 * <ReelspotSymbol3D
 *   symbolId="wild"
 *   position={[0, 0, 0]}
 *   isWinning={true}
 *   reelspotBackground={skin.reelspotBackground}
 *   winAnimationType="pulse"
 *   winAnimationConfig={{ intensity: 0.15 }}
 * />
 * ```
 */
export function ReelspotSymbol3D({
  symbolId,
  position = [0, 0, 0],
  scale = 1,
  isWinning = false,
  glowIntensity = 0,
  animate = false,
  reelspotBackground,
  winAnimationType = 'pulse',
  winAnimationConfig = {},
  reelspotWidth = 1.0,
  reelspotHeight = 1.0,
  resolvedSymbol,
}: ReelspotSymbol3DProps) {
  const { getSymbol } = useSlotTheme();
  const reelspotRef = useRef<THREE.Group>(null);

  // Get symbol from either resolved skin or theme
  const symbol = resolvedSymbol || getSymbol(symbolId);

  // Determine animation settings
  const animationType = resolvedSymbol?.winAnimation?.type || winAnimationType;
  const animationConfig = getMergedAnimationConfig(
    animationType,
    resolvedSymbol?.winAnimation?.config || winAnimationConfig
  );

  // Apply win animation to the reelspot group
  useWinAnimation(reelspotRef, {
    type: animationType,
    config: animationConfig,
    isActive: isWinning,
    glowColor: symbol?.glowColor || '#FFD700',
    loop: true, // Loop until win display ends
  });

  return (
    <group ref={reelspotRef} position={position}>
      {/* Reelspot background (behind symbol) */}
      <ReelspotBackground
        background={reelspotBackground}
        width={reelspotWidth}
        height={reelspotHeight}
        isWinning={isWinning}
        winGlowColor={symbol?.glowColor || '#FFD700'}
      />

      {/* The actual symbol */}
      <Symbol3D
        symbolId={symbolId}
        position={[0, 0, 0]}
        scale={scale}
        isWinning={false} // Win animation handled by reelspot group
        glowIntensity={glowIntensity}
        animate={animate}
      />
    </group>
  );
}

// =============================================================================
// Convenience hook for getting win animation config
// =============================================================================

/**
 * Hook to get the appropriate win animation config for a symbol
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSymbolWinAnimationConfig(
  symbolId: SymbolId,
  resolvedSymbol?: ResolvedSkinSymbol
): {
  type: WinAnimationPresetId;
  config: Record<string, unknown>;
} {
  const { getSymbol } = useSlotTheme();
  const symbol = resolvedSymbol || getSymbol(symbolId);

  return useMemo(() => {
    if (resolvedSymbol?.winAnimation) {
      return {
        type: resolvedSymbol.winAnimation.type,
        config: getMergedAnimationConfig(
          resolvedSymbol.winAnimation.type,
          resolvedSymbol.winAnimation.config
        ),
      };
    }

    // Default animation based on symbol type
    const defaultType: WinAnimationPresetId = symbol?.isWild ? 'glow' : 'pulse';
    return {
      type: defaultType,
      config: DEFAULT_ANIMATION_CONFIGS[defaultType],
    };
  }, [resolvedSymbol, symbol]);
}

export default Symbol3D;
